import { type ReactNode, useState } from 'react';
import { makeStyles, mergeClasses, tokens } from '@fluentui/react-components';

const useStyles = makeStyles({
  group: {
    display: 'inline-flex',
    verticalAlign: 'middle',
  },
  main: {
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
    paddingRight: '2.5px',
    minWidth: 0,
  },
  chevron: {
    width: 'fit-content',
    padding: 0,
    paddingTop: '4px',
    fontSize: '.5rem',
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
  },
  hot: {
    backgroundColor: `${tokens.colorNeutralBackground1Hover} !important`,
  },
  chevronDisabled: {
    '&:disabled, &[aria-disabled="true"]': {
      backgroundColor: 'transparent !important',
      boxShadow: 'none !important',
    },
    '&:disabled:hover, &[aria-disabled="true"]:hover': {
      backgroundColor: 'transparent !important',
    },
    '&:disabled:active, &[aria-disabled="true"]:active': {
      backgroundColor: 'transparent !important',
    },
    '&:disabled:focus-visible, &[aria-disabled="true"]:focus-visible': {
      outline: 'none !important',
      boxShadow: 'none !important',
    },
  },
});

type TProps = {
  /** Keeps both halves highlighted, e.g. while the chevron's menu is open */
  isOpen?: boolean;
  isChevronDisabled?: boolean;
  /** Renders the main action button with the given class name */
  renderMain: (className: string) => ReactNode;
  /** Renders the chevron button with the given class name */
  renderChevron: (className: string) => ReactNode;
};

/** Shell of a toolbar split button: a main action and a chevron that highlight together. */
const ToolbarSplitButton = ({ isOpen = false, isChevronDisabled = false, renderMain, renderChevron }: TProps) => {
  const [hovered, setHovered] = useState(false);
  const hot = hovered || isOpen;

  const classes = useStyles();
  return (
    <div
      className={classes.group}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onBlurCapture={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node)) setHovered(false);
      }}
    >
      {renderMain(mergeClasses(classes.main, hot && classes.hot))}
      {renderChevron(mergeClasses(classes.chevron, isChevronDisabled && classes.chevronDisabled, hot && classes.hot))}
    </div>
  );
};

export default ToolbarSplitButton;
